/**
 * Serviço de Notificações em Tempo Real via WebSocket
 *
 * Responsabilidades:
 * - Conectar ao servidor WebSocket com autenticação
 * - Gerenciar subscrição de alertas por proximidade
 * - Enviar localização periodicamente ao backend
 * - Receber e rotear notificações em tempo real
 * - Filtrar notificações por roles do usuário
 * - Reconexão automática com exponential backoff
 * - Heartbeat para manter conexão viva
 *
 * Baseado em ADR-0003 - Notificações Realtime
 *
 * @module core/notifications/services
 */

import { Injectable, inject, signal, OnDestroy, effect } from '@angular/core';
import { Subject, take } from 'rxjs';
import { AuthService } from '../../auth/services/auth.service';
import { GeolocationService } from '../../geolocation/services/geolocation.service';
import { NotificationStore } from './notification.store';
import { NotificationPreferencesService } from './notification-preferences.service';
import { environment } from '../../../../environments/environment';
import { FeatureFlagService } from '../../feature-flags';
import {
  NotificationType,
  NotificationPriority,
  ProximityAlert,
  ProximitySubscriptionConfig,
  ConnectionStatus,
  WsMessageType,
  WsResponseType,
  RECONNECT_CONFIG,
  HEARTBEAT_INTERVAL_MS,
  LOCATION_UPDATE_INTERVAL_MS,
} from '../models/notification.model';

/**
 * Interface de notificação realtime (legado, mantido para compatibilidade)
 */
export interface RealtimeNotification {
  type: NotificationType;
  id: string;
  priority: NotificationPriority;
  timestamp: string;
  payload: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class RealtimeService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly notificationStore = inject(NotificationStore);
  private readonly preferencesService = inject(NotificationPreferencesService);
  private readonly flagService = inject(FeatureFlagService);

  private ws: WebSocket | null = null;
  private readonly notificationSubject = new Subject<RealtimeNotification>();
  private readonly connectionStatus = signal<ConnectionStatus>('disconnected');

  /** Observable de notificações (legado, mantido para compatibilidade) */
  readonly notifications$ = this.notificationSubject.asObservable();
  /** Status da conexão como signal readonly */
  readonly isConnected = this.connectionStatus.asReadonly();

  // Timers internos
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private locationUpdateInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  // Estado de subscrição
  private isProximitySubscribed = false;
  private lastToken: string | null = null;

  constructor() {
    // Sincroniza status da conexão com o store
    effect(() => {
      this.notificationStore.setConnectionStatus(this.connectionStatus());
    });
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  /**
   * Conecta ao servidor WebSocket.
   * Requer flag `websocketRealtime` habilitada.
   */
  connect(): void {
    if (!this.flagService.isEnabled('websocketRealtime')) {
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }

    this.authService.getAccessToken().pipe(take(1)).subscribe(token => {
      if (!token) {
        console.warn('[REALTIME] Sem token de autenticação');
        return;
      }
      this.lastToken = token;
      this.connectionStatus.set('connecting');
      this.establishConnection(token);
    });
  }

  /**
   * Desconecta do servidor WebSocket
   */
  disconnect(): void {
    this.clearTimers();
    this.isProximitySubscribed = false;
    this.notificationStore.setSubscribed(false);
    this.reconnectAttempts = 0;

    if (this.ws) {
      this.ws.onclose = null; // Evita reconnect automático
      this.ws.close();
      this.ws = null;
    }

    this.connectionStatus.set('disconnected');
  }

  /**
   * Inscreve o policial para receber alertas por proximidade.
   * Obtém a localização atual e envia ao backend com a configuração.
   */
  async subscribeProximity(config?: ProximitySubscriptionConfig): Promise<void> {
    const cfg = config || this.preferencesService.config();

    if (!cfg.enabled) {
      return;
    }

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[REALTIME] WebSocket não conectado para subscribe');
      return;
    }

    const user = this.authService.user();
    if (!user) {
      return;
    }

    // Obtém localização atual
    const coords = await this.geolocationService.getCoordenadasRapido();

    this.sendMessage({
      type: WsMessageType.SUBSCRIBE_PROXIMITY,
      userId: user.sub,
      token: this.lastToken,
      location: {
        latitude: coords.latitude,
        longitude: coords.longitude,
      },
      radiusKm: cfg.radiusKm,
      alertTypes: cfg.alertTypes,
    });

    this.isProximitySubscribed = true;
    this.notificationStore.setSubscribed(true);

    // Inicia envio periódico de localização
    this.startLocationUpdates();
  }

  /**
   * Cancela a subscrição de alertas por proximidade
   */
  unsubscribeProximity(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.sendMessage({ type: WsMessageType.UNSUBSCRIBE_PROXIMITY });

    this.isProximitySubscribed = false;
    this.notificationStore.setSubscribed(false);
    this.stopLocationUpdates();
  }

  /**
   * Envia atualização de localização ao backend
   */
  async updateLocation(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isProximitySubscribed) {
      return;
    }

    const coords = await this.geolocationService.getCoordenadasRapido();

    this.sendMessage({
      type: WsMessageType.UPDATE_LOCATION,
      location: {
        latitude: coords.latitude,
        longitude: coords.longitude,
      },
    });
  }

  /**
   * Verifica se usuário deve receber a notificação com base em roles
   */
  shouldReceiveNotification(notification: RealtimeNotification): boolean {
    const user = this.authService.user();

    if (!user) {
      return false;
    }

    switch (notification.type) {
      case NotificationType.ALERTA_DESAPARECIDO:
      case NotificationType.ALERTA_PROCURADO:
        return this.authService.hasAnyRole(['app.sentinela.acesso', 'app.sentinela.facial']);

      case NotificationType.ALERTA_VEICULO:
      case NotificationType.ALERTA_PROPRIETARIO_PROCURADO:
        return this.authService.hasAnyRole(['app.sentinela.acesso', 'app.sentinela.veicular']);

      case NotificationType.CHAMADO_190:
      case NotificationType.DESPACHO_EMERGENCIA:
        return this.authService.hasRole('app.nexus.acesso');

      default:
        return true;
    }
  }

  /**
   * Ajusta prioridade baseado na unidade do usuário
   */
  adjustPriorityByUnit(notification: RealtimeNotification): NotificationPriority {
    const user = this.authService.user();

    if (!user) {
      return notification.priority;
    }

    const payload = notification.payload as Record<string, unknown> | null;
    if (payload?.['unidadeCod'] === user.unidadeCod) {
      if (notification.priority === NotificationPriority.MEDIUM) {
        return NotificationPriority.HIGH;
      }
      if (notification.priority === NotificationPriority.LOW) {
        return NotificationPriority.MEDIUM;
      }
    }

    return notification.priority;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PRIVADOS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Estabelece conexão WebSocket com exponential backoff
   */
  private establishConnection(token: string): void {
    const wsUrl = `${environment.websocket?.url || 'ws://localhost:3000/ws'}?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[REALTIME] Conectado');
        this.connectionStatus.set('connected');
        this.reconnectAttempts = 0;
        this.sendAuthentication(token);
        this.startHeartbeat();

        // Auto-subscribe se as preferências estiverem habilitadas
        const config = this.preferencesService.config();
        if (config.enabled) {
          this.subscribeProximity(config);
        }
      };

      this.ws.onmessage = (event) => {
        this.handleMessage(event.data);
      };

      this.ws.onerror = (error) => {
        console.error('[REALTIME] Erro na conexão:', error);
      };

      this.ws.onclose = () => {
        console.log('[REALTIME] Conexão fechada');
        this.connectionStatus.set('disconnected');
        this.isProximitySubscribed = false;
        this.notificationStore.setSubscribed(false);
        this.clearTimers();
        this.scheduleReconnect();
      };
    } catch (error) {
      console.error('[REALTIME] Erro ao criar WebSocket:', error);
      this.connectionStatus.set('disconnected');
      this.scheduleReconnect();
    }
  }

  /**
   * Envia autenticação ao servidor
   */
  private sendAuthentication(token: string): void {
    const user = this.authService.user();

    this.sendMessage({
      type: WsMessageType.AUTH,
      token,
      userId: user?.sub,
      unidadeCod: user?.unidadeCod,
      roles: user?.roles.realm || [],
    });
  }

  /**
   * Processa mensagem recebida do WebSocket
   */
  private handleMessage(data: string): void {
    try {
      const message = JSON.parse(data);

      switch (message.type) {
        case WsResponseType.AUTH_OK:
          console.log('[REALTIME] Autenticado com sucesso');
          break;

        case WsResponseType.AUTH_ERROR:
          console.error('[REALTIME] Erro de autenticação:', message.message);
          this.disconnect();
          break;

        case WsResponseType.SUBSCRIBE_PROXIMITY_OK:
          console.log('[REALTIME] Subscrição de proximidade confirmada', {
            subscriptionId: message.subscriptionId,
            radiusKm: message.radiusKm,
          });
          break;

        case WsResponseType.PROXIMITY_ALERT:
          this.handleProximityAlert(message);
          break;

        case WsResponseType.PONG:
          // Heartbeat respondido
          break;

        default:
          // Notificação genérica (legado)
          if (message.type && message.id) {
            this.handleLegacyNotification(message);
          }
          break;
      }
    } catch (error) {
      console.error('[REALTIME] Erro ao processar mensagem:', error);
    }
  }

  /**
   * Processa alerta de proximidade recebido
   */
  private handleProximityAlert(message: Record<string, unknown>): void {
    console.debug('[NOTIFICATIONS:WS] Alerta de proximidade recebido:', message);

    const alert: ProximityAlert = {
      id: message['id'] as string,
      alertType: message['alertType'] as NotificationType,
      operationalType: (message['operationalType'] as string) || '',
      priority: (message['priority'] as NotificationPriority) || NotificationPriority.MEDIUM,
      timestamp: (message['timestamp'] as string) || new Date().toISOString(),
      crimeType: (message['crimeType'] as string) || '',
      criminalityLevel: (message['criminalityLevel'] as number) || 3,
      distance: (message['distance'] as ProximityAlert['distance']) || { km: 0, formattedText: '' },
      location: (message['location'] as ProximityAlert['location']) || {
        description: '',
        coordinates: { latitude: 0, longitude: 0 },
      },
      summary: (message['summary'] as string) || '',
      person: message['person'] as ProximityAlert['person'],
      vehicle: message['vehicle'] as ProximityAlert['vehicle'],
      elapsedTime: (message['elapsedTime'] as string) || '',
      read: false,
    };

    console.debug('[NOTIFICATIONS:WS] Alerta mapeado para store:', { id: alert.id, type: alert.alertType, summary: alert.summary });
    this.notificationStore.addNotification(alert);

    // Emite também no Subject legado para compatibilidade
    const legacyNotification: RealtimeNotification = {
      type: alert.alertType,
      id: alert.id,
      priority: alert.priority,
      timestamp: alert.timestamp,
      payload: message,
    };

    if (this.shouldReceiveNotification(legacyNotification)) {
      this.notificationSubject.next(legacyNotification);
    }
  }

  /**
   * Processa notificação no formato legado
   */
  private handleLegacyNotification(message: Record<string, unknown>): void {
    const notification: RealtimeNotification = {
      type: message['type'] as NotificationType,
      id: message['id'] as string,
      priority: (message['priority'] as NotificationPriority) || NotificationPriority.MEDIUM,
      timestamp: (message['timestamp'] as string) || new Date().toISOString(),
      payload: message['payload'] || message,
    };

    if (this.shouldReceiveNotification(notification)) {
      this.notificationSubject.next(notification);
    }
  }

  /**
   * Envia mensagem JSON pelo WebSocket
   */
  private sendMessage(payload: Record<string, unknown>): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      this.ws.send(JSON.stringify(payload));
    } catch (error) {
      console.error('[REALTIME] Erro ao enviar mensagem:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TIMERS E HEARTBEAT
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Inicia heartbeat periódico (PING a cada 30s)
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = setInterval(() => {
      this.sendMessage({ type: WsMessageType.PING });
    }, HEARTBEAT_INTERVAL_MS);
  }

  /**
   * Para o heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Inicia envio periódico de localização (a cada 30s)
   */
  private startLocationUpdates(): void {
    this.stopLocationUpdates();
    this.locationUpdateInterval = setInterval(() => {
      this.updateLocation();
    }, LOCATION_UPDATE_INTERVAL_MS);
  }

  /**
   * Para envio periódico de localização
   */
  private stopLocationUpdates(): void {
    if (this.locationUpdateInterval) {
      clearInterval(this.locationUpdateInterval);
      this.locationUpdateInterval = null;
    }
  }

  /**
   * Agenda reconexão com exponential backoff
   */
  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (!this.authService.isAuthenticated()) {
      return;
    }

    const delay = Math.min(
      RECONNECT_CONFIG.initialDelayMs * Math.pow(RECONNECT_CONFIG.multiplier, this.reconnectAttempts),
      RECONNECT_CONFIG.maxDelayMs
    );

    console.log(`[REALTIME] Reconectando em ${delay}ms (tentativa ${this.reconnectAttempts + 1})`);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Limpa todos os timers
   */
  private clearTimers(): void {
    this.stopHeartbeat();
    this.stopLocationUpdates();
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }
}
